// account.ts — Account (session/auth record), ProfileOverrides, User/UserProfile (registry record)
import type { VideoChannel } from './post';

/**
 * Notification categories a member can configure, in the order Settings shows them.
 *
 * Council/moderation notifications are deliberately absent: under-review,
 * decisions, and appeal outcomes always reach the user, so there is no switch
 * to model. Mirrors NOTIFY_CATEGORIES in backend/apps/accounts/models.py.
 */
export const NOTIFICATION_CATEGORIES = [
  'posts',
  'dms',
  'comments',
  'likes',
  'cherishes',
  'spreads',
  'mentions',
  'follows',
  'quotes',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/**
 * Per-category delivery switches.
 *
 * `inApp` and `push` are not fully independent server-side: push is generated
 * from the in-app row, so in-app off + push on still writes the row and hides it
 * from the list. Both off suppresses it entirely.
 */
export interface NotificationChannels {
  inApp: boolean;
  push: boolean;
}

export type NotificationPrefs = Record<NotificationCategory, NotificationChannels>;

/** One followed account plus its per-creator post-notification switch. */
export interface FollowedCreator {
  handle: string;
  name: string;
  avatarKey: string;
  verified: boolean;
  notifyPosts: boolean;
}

/** One muted account, for the Settings → Content Preferences manage list.
 *  Mirrors the backend's serialize_suggested shape (see SuggestedProfile)
 *  plus `premium`, which that shape doesn't carry. */
export interface MutedAccount {
  id: string;
  name: string;
  handle: string;
  av: string;
  verified: boolean;
  premium: boolean;
  followerCount: number;
}

/**
 * 1.27.70 Edit Profile: who sees a birthday. `private` (the default) sends
 * nothing to anyone else; `month_day` never includes the year. Mirrors
 * Profile.BIRTHDAY_VISIBILITY in backend/apps/accounts/models.py.
 */
export type BirthdayVisibility = 'private' | 'month_day' | 'full';

/** What other people see of a birthday: absent unless the member chose to show it. */
export interface BirthdayShown {
  month: number;
  day: number;
  year?: number;
}

/** Limits enforced by backend/apps/accounts/profile_fields.py. Keep the two in step. */
export const PROFILE_LIMITS = {
  displayName: 50,
  bio: 160,
  pronouns: 30,
  creatorType: 40,
  tagline: 100,
  links: 5,
  linkLabel: 40,
  linkHref: 200,
  minAge: 18,
} as const;

// The five keys that drive profile content. All optional — only provided fields are overridden.
export interface ProfileOverrides {
  displayName?: string;
  bio?: string;
  location?: string;
  /** { label, href } pair or null to clear */
  link?: { label: string; href: string } | null;
  /** 1.27.70 Edit Profile. Empty string clears. */
  pronouns?: string;
  creatorType?: string;
  tagline?: string;
  /** ISO date (YYYY-MM-DD), or null to clear. */
  birthday?: string | null;
  birthdayVisibility?: BirthdayVisibility;
  /** Up to PROFILE_LIMITS.links, in display order. */
  links?: ProfileLink[];
  avatarKey?: string;
  bannerTone?: string;
  likesPublic?: boolean;
  /** R2 object key of an uploaded avatar image (empty string clears it back to the gradient). */
  avatarImageKey?: string;
  /** R2 object key of an uploaded banner image (empty string clears it). */
  bannerImageKey?: string;
  /** Privacy & Security — PATCHed through the same /users/me/ seam. */
  privateAccount?: boolean;
  /** 1.27.60 "Your channel": who sees your videos when a video keeps its default. */
  videoChannel?: VideoChannel;
  /** Keep existing followers when flipping public→private. Default true; when
   * false, the flip demotes current followers to pending follow requests. */
  keepFollowersOnPrivate?: boolean;
  dmPolicy?: 'everyone' | 'following' | 'nobody';
  mentionPolicy?: 'everyone' | 'following' | 'nobody';
  suggestible?: boolean;
  loginAlerts?: boolean;
  /**
   * Require the recipient's acceptance before a group-add takes effect. Default
   * true server-side. When false, a direct add still degrades to a pending
   * invite unless the adder would also be admitted as a DM under `dmPolicy` —
   * this is what stops group-add from bypassing `dmPolicy`.
   */
  groupInvitesRequired?: boolean;
  /** Partial by design — send only the categories/channels that changed. */
  notificationPrefs?: Partial<Record<NotificationCategory, Partial<NotificationChannels>>>;
  /**
   * Regional interests. Note there is deliberately NO baseRegion here: the
   * account region is immutable and the server refuses to write it, so offering
   * it as an override would be a control wired to nothing.
   */
  regionInterests?: string[];
  regionPublic?: boolean;
  /**
   * Disabled For-You topic keys (empty = every topic on). 'general' is not a
   * valid member — the server rejects it, it always stays on.
   */
  feedTopics?: string[];
  /**
   * "Why am I seeing this?" transparency (1.21.00 Feature 4). When true, every
   * For-You post card renders an always-visible reason line instead of only
   * exposing it behind the ··· menu. Default false.
   */
  showFeedReasons?: boolean;
  /** Videos (2026-09-26): pre-tick "Post as a Short" for clips of 60 s or less. Default on. */
  autoShortClassifier?: boolean;
  /** Videos (2026-09-26): play the next Up Next video automatically. Default off. */
  autoplayNext?: boolean;
  /**
   * 1.24.00 onboarding: POSITIVE topic keys the member said they are into
   * (boosts For You + suggested people). Distinct from `feedTopics`, which is
   * the opt-OUT list — writing picks there would HIDE the chosen topics.
   * 'general' is rejected exactly like feedTopics.
   */
  interests?: string[];
  /**
   * 1.24.00 onboarding: `true` marks the flow done (server stamps the time).
   * Never un-set — the server ignores anything but `true`.
   */
  onboardedAt?: true;
  /**
   * 1.24.00 DMs: send read receipts. RECIPROCAL — off means you don't send
   * them AND you stop seeing anyone else's. Default true.
   */
  shareReadReceipts?: boolean;
  /**
   * 1.24.00 DMs: show when you're active. RECIPROCAL — off means you never
   * show as online or typing AND you stop seeing anyone else's. Default true.
   */
  showActivityStatus?: boolean;
}

/** `GET /users/topics/` — the 8 topic keys + labels, plus the onboarding minimum. */
export interface TopicOption {
  key: string;
  label: string;
}

export interface TopicsPayload {
  topics: TopicOption[];
  minInterests: number;
}

/** `GET /users/me/invite/` — the member's one active personal invite link. */
export interface InviteLink {
  code: string;
  url: string;
  usesCount: number;
}

/** `GET /invites/<code>/` — who a public invite link belongs to. Never carries an email. */
export interface InviteInfo {
  inviter: { name: string; handle: string; av: string; verified: boolean };
  valid: true;
}

// A persisted auth account record (stored in localStorage['truxsocial:accounts']).
// `password` is plaintext — accepted prototype constraint.
export interface Account {
  userId: string;
  username: string;
  email: string | null;
  displayName: string;
  handle: string;
  password: string;
  joinedAt: string; // ISO-8601
  useThisAsActive: boolean;
  /** Whether this account has an active premium subscription. */
  premium?: boolean;
  profile?: ProfileOverrides;
}

// Display stats object on a user's profile (following/followers/posts/likes).
// All values are integers. Display formatting (e.g. 129000 → '129K') is a display-layer
// concern. The data port converts legacy display-string seeds (e.g. '129K') to integers.
export interface UserStats {
  following: number;
  followers: number;
  posts: number;
  likes: number;
}

// "About" row (icon + text blurb) used in the profile sidebar.
export interface AboutRow {
  icon: string;
  text: string;
}

// Achievement badge displayed on a profile.
export interface Achievement {
  id: string;
  name: string;
  tone: 'gold' | 'blue' | 'violet' | string;
}

// Stats overview widget (headline stats + sparkline data).
export interface StatsOverview {
  headline: Array<{ label: string; value: string }>;
  spark: number[];
}

// Mutual-followers display.
export interface Mutuals {
  count: number;
  avatars: Array<{ name: string; av: string }>;
}

// Link record on a user profile.
export interface ProfileLink {
  label: string;
  href: string;
}

// Full user profile registry record (the combined legacy `users` map value).
// Some fields are optional — only present on fully-featured static users, not
// auto-generated sign-up records.
export interface User {
  id: string;
  handle: string;
  name: string;
  avatarKey: string;
  avatarInitial: string;
  verified: boolean;
  premium: boolean;
  /**
   * 1.23.00 Phase 2: a supporter badge, visually distinct from `verified`.
   * DERIVED server-side (has_pro(user) AND confirmed 2FA) — never stored,
   * never computed client-side. A Pro user without 2FA still has
   * `premium: true` but `supporterBadge: false` — they keep the entitlement,
   * only the badge is gated. See apps.billing.entitlements.has_supporter_badge.
   * Optional (like twoFactorEnabled/badgeGraceExpiresAt below) — absent is
   * falsy, never renders.
   */
  supporterBadge?: boolean;
  /**
   * 1.23.00 Phase 6: cosmetic profile flair, Pro-gated to render. One of
   * 'none' | 'aurora-ring' | 'ember-ring' | 'cyan-ring'. Purely decorative —
   * never gates speech, never read by ranking. The server already zeroes
   * this to 'none' for a non-Pro account, so no client-side gating is needed.
   * Optional — absent/undefined means "render nothing", same as 'none'.
   */
  flair?: string;
  /** True when the account is platform staff (Django is_staff). Drives the Owner Panel. */
  isAdmin: boolean;
  /** True when the account is a Django superuser (destructive owner actions). */
  isSuperuser: boolean;
  /** Account moderation status. */
  status?: 'active' | 'suspended' | 'banned';
  statusReason?: string;
  statusCategory?: string;
  bio: string;
  role: string;
  location?: string;
  link?: ProfileLink | null;
  /** 1.27.70 Edit Profile: public once filled in (absent on payloads from older servers). */
  pronouns?: string;
  creatorType?: string;
  tagline?: string;
  links?: ProfileLink[];
  /** Owner payloads only: the stored birthday (ISO date) and who may see it. */
  birthday?: string | null;
  birthdayVisibility?: BirthdayVisibility;
  /** Present only when the member chose to show their birthday; never more than they chose. */
  birthdayPublic?: BirthdayShown;
  banner?: string;
  bannerTone?: string;
  tags?: string[];
  joined: string; // display string, e.g. 'January 2024'
  followerCount?: number; // counts live here; display formatting is a display-layer concern
  stats?: UserStats;
  about?: AboutRow[];
  aboutThisUser?: AboutRow[];
  topicPills?: string[];
  /** Backend serialize_user emits null (not undefined) when the profile has no mutuals. */
  mutuals?: Mutuals | null;
  achievements?: Achievement[];
  statsOverview?: StatsOverview;
  likedPostIds: string[];
  likesPublic: boolean;
  /** Whether the account's email is confirmed (Django email_verified). Gates writes. */
  emailVerified?: boolean;
  /** Contact & recovery — present ONLY on the owner's own payloads (auth/me,
      login, /users/me/*). The public /users/<username>/ payload never carries them. */
  phone?: string;
  phoneVerified?: boolean;
  backupEmail?: string;
  backupEmailVerified?: boolean;
  /** Privacy & Security preferences — owner payloads only, enforced server-side. */
  privateAccount?: boolean;
  /**
   * 1.27.60 "Your channel". PUBLIC like privateAccount: a private account's non-follower
   * still gets a Videos tab when this is 'public' (the server decides what is in it).
   */
  videoChannel?: VideoChannel;
  /** Keep existing followers when flipping public→private. Default true; when
   * false, the flip demotes current followers to pending follow requests. */
  keepFollowersOnPrivate?: boolean;
  dmPolicy?: 'everyone' | 'following' | 'nobody';
  mentionPolicy?: 'everyone' | 'following' | 'nobody';
  suggestible?: boolean;
  loginAlerts?: boolean;
  /** Require acceptance before a group-add takes effect. Owner payloads only. */
  groupInvitesRequired?: boolean;
  /** Per-category notification switches. Owner payloads only. */
  notificationPrefs?: NotificationPrefs;
  /**
   * Account region — the country the account was opened in, ISO 3166-1 alpha-2,
   * or '' when the edge supplied none. READ-ONLY: it is written once at
   * registration and has no write path. Owner payloads only.
   */
  baseRegion?: string;
  /** Regional interests — places to see more content from. Owner payloads only. */
  regionInterests?: string[];
  /** Whether the account region is published on the profile. Default false. */
  regionPublic?: boolean;
  /**
   * Another member's published region, present on a PUBLIC payload only when
   * that member switched `regionPublic` on. Absent otherwise — a viewer cannot
   * tell "kept private" from "never detected", which is deliberate.
   */
  region?: string;
  /**
   * For-You feed: disabled topic keys (empty = every topic on). Owner payloads
   * only. 'general' is never a member — it always stays on.
   */
  feedTopics?: string[];
  /** See User.showFeedReasons (1.21.00 Feature 4). Owner payloads only. */
  showFeedReasons?: boolean;
  /** Videos settings — see ProfileOverrides. Owner payloads only. */
  autoShortClassifier?: boolean;
  autoplayNext?: boolean;
  /**
   * 1.22.00: whether this account has confirmed TOTP two-factor. Owner
   * payloads ONLY — publicly exposing per-account 2FA status is an attacker
   * signal (which accounts are/aren't a soft target), so the backend never
   * serializes this on the public /users/<username>/ payload.
   */
  twoFactorEnabled?: boolean;
  /**
   * 1.22.00 badge⇔2FA enforcement: present ONLY while the migration-cohort
   * 14-day grace countdown is running (an ISO timestamp), absent (not null)
   * once cleared — same convention as `region` above. Drives the
   * "enable 2FA or lose your badge" shell banner.
   */
  badgeGraceExpiresAt?: string;
  /**
   * 1.24.00 onboarding — owner payloads only. Positive topic keys the member
   * picked (see ProfileOverrides.interests). Absent on public payloads.
   */
  interests?: string[];
  /**
   * 1.24.00 onboarding — owner payloads only. ISO time the welcome flow was
   * completed/skipped, or `null` for a brand-new member who has not seen it.
   * STRICT null drives the redirect: `undefined`/absent (older cached users,
   * public payloads, every pre-1.24 test fixture) must NEVER trigger it.
   */
  onboardedAt?: string | null;
  /**
   * 1.24.00 invites — owner payloads only (the API must not leak anyone's
   * graph). How many members joined through this account's invite link. Rendered
   * as the 5th stat cell on the OWN profile only; other profiles never carry it.
   */
  invited?: number;
  /** 1.24.00 invites — owner payloads only. Who invited this member, or null. */
  invitedBy?: { handle: string; name: string } | null;
  /**
   * 1.24.00 DMs — owner payloads only. Send read receipts (reciprocal: off also
   * stops you seeing other people's). Default true server-side, so `undefined`
   * must read as ON.
   */
  shareReadReceipts?: boolean;
  /**
   * 1.24.00 DMs — owner payloads only. Show when you're active (reciprocal: off
   * also hides everyone else's dot and typing line from you). Default true
   * server-side, so `undefined` must read as ON.
   */
  showActivityStatus?: boolean;
}

// Alias — the plan references both names. User is the canonical type.
export type UserProfile = User;

// Lightweight suggestion record used in the Who-to-Follow / Suggested rails.
// Mirrors the backend `serialize_suggested` shape: `handle` already includes the
// leading "@" (do NOT re-prefix it in the UI); `followerCount` is an integer and
// display formatting (e.g. 8400 → "8.4K") is a UI-layer concern.
export interface SuggestedProfile {
  id: string;
  name: string;
  handle: string;
  av: string;
  verified: boolean;
  followerCount: number;
}
